import { useEffect, useState } from "react";
import { ArrowUpRight, Download, Loader2, Monitor } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { FintheonBrandLockup } from "../brand/FintheonBrandLockup";
import {
  fetchLatestDesktopDownload,
  formatDesktopDownloadSize,
  PUBLIC_MAC_DOWNLOAD_URL,
  type DesktopDownload,
} from "../../lib/desktop-download";

type DownloadState =
  | { status: "loading"; download: null }
  | { status: "ready"; download: DesktopDownload }
  | { status: "error"; download: null };

export function BetaDesktopDownloadPage() {
  const { user, signOut } = useAuth();
  const [state, setState] = useState<DownloadState>({
    status: "loading",
    download: null,
  });

  useEffect(() => {
    document.title = "Download Fintheon for Mac";

    let active = true;
    void fetchLatestDesktopDownload()
      .then((download) => {
        if (active) setState({ status: "ready", download });
      })
      .catch(() => {
        if (active) setState({ status: "error", download: null });
      });

    return () => {
      active = false;
    };
  }, []);

  const size =
    state.status === "ready"
      ? formatDesktopDownloadSize(state.download.size)
      : null;

  return (
    <main className="beta-download-page">
      <style>{betaDownloadCss}</style>
      <header className="beta-download-page__header">
        <FintheonBrandLockup
          ariaLabel="Fintheon"
          className="beta-download-page__brand"
          iconClassName="beta-download-page__brand-icon"
          textClassName="beta-download-page__brand-text"
        />
        <span className="beta-download-page__eyebrow">macOS download</span>
      </header>

      <section
        className="beta-download-page__body"
        aria-labelledby="beta-title"
      >
        <div className="beta-download-page__signal" aria-hidden="true">
          <Monitor size={24} strokeWidth={1.45} />
        </div>
        <p className="beta-download-page__kicker">Current desktop release</p>
        <h1 id="beta-title">Fintheon for Mac.</h1>
        <p className="beta-download-page__summary">
          Download the disk image, open it, and drag Fintheon to Applications.
          The file is delivered directly without a GitHub account.
        </p>

        <div className="beta-download-page__download" aria-live="polite">
          <a
            className="beta-download-page__primary fintheon-black-liquid-glass fintheon-primary-action"
            href={PUBLIC_MAC_DOWNLOAD_URL}
          >
            <Download size={17} aria-hidden="true" />
            Download Fintheon for Mac
          </a>
          {state.status === "loading" && (
            <span className="beta-download-page__loading">
              <Loader2 size={16} aria-hidden="true" />
              Checking the current desktop build
            </span>
          )}
          {state.status === "ready" && (
            <p className="beta-download-page__metadata">
              macOS · v{state.download.version}
              {size ? ` · ${size}` : ""}
            </p>
          )}
          {state.status === "error" && (
            <p className="beta-download-page__error">
              Release details could not be loaded, but the download above still
              resolves to the current macOS build.
            </p>
          )}
        </div>

        <div className="beta-download-page__actions">
          <a
            href="https://app.fintheon.io/"
            className="beta-download-page__link"
          >
            Open Fintheon on the web{" "}
            <ArrowUpRight size={14} aria-hidden="true" />
          </a>
          {user && (
            <button type="button" onClick={() => void signOut()}>
              Sign out {user.email ? `(${user.email})` : ""}
            </button>
          )}
        </div>
      </section>
    </main>
  );
}

const betaDownloadCss = `
  .beta-download-page {
    box-sizing: border-box;
    display: grid;
    min-height: 100vh;
    grid-template-rows: auto 1fr;
    padding: clamp(1.35rem, 3vw, 2.5rem);
    overflow: hidden;
    background: #050402;
    color: var(--fintheon-text, #f0ead6);
  }
  .beta-download-page *, .beta-download-page *::before, .beta-download-page *::after { box-sizing: border-box; }
  .beta-download-page__header { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
  .beta-download-page__brand { display: inline-flex; align-items: center; gap: 0.5rem; color: #d7d1bd; }
  .beta-download-page__brand-icon { width: 1.08rem; height: 1.08rem; }
  .beta-download-page__brand-text, .beta-download-page__eyebrow, .beta-download-page__kicker {
    font-family: "Doto", "Readable Digits", ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    text-transform: uppercase;
  }
  .beta-download-page__brand-text { font-size: 0.78rem; font-weight: 760; letter-spacing: 0.24em; }
  .beta-download-page__eyebrow { color: #aa8b43; font-size: 0.68rem; font-weight: 720; letter-spacing: 0.12em; }
  .beta-download-page__body { align-self: center; width: min(34rem, 100%); margin: 0 auto; padding: clamp(2rem, 7vh, 5rem) 0; }
  .beta-download-page__signal { display: flex; color: #c79f4a; }
  .beta-download-page__kicker { margin: 2.2rem 0 0; color: #aa8b43; font-size: 0.69rem; font-weight: 720; letter-spacing: 0.14em; }
  .beta-download-page h1 { max-width: 10ch; margin: 0.62rem 0 0; font-family: "Iowan Old Style", Georgia, serif; font-size: clamp(2.65rem, 7vw, 4.7rem); font-weight: 500; letter-spacing: 0; line-height: 0.92; text-wrap: balance; }
  .beta-download-page__summary { max-width: 33rem; margin: 1.35rem 0 0; color: rgba(240, 234, 214, 0.66); font-size: 1rem; line-height: 1.55; }
  .beta-download-page__download { min-height: 4.2rem; margin-top: 2rem; }
  .beta-download-page__primary { display: inline-flex; min-height: 3.45rem; align-items: center; justify-content: center; gap: 0.65rem; border-radius: 8px; padding: 0.75rem 1.2rem; color: var(--fintheon-text, #f0ead6); font-size: 0.85rem; font-weight: 760; text-decoration: none; transition: color 160ms ease, background-color 160ms ease; }
  .beta-download-page__primary:hover { color: #fff8e5; }
  .beta-download-page__metadata, .beta-download-page__loading, .beta-download-page__error { margin: 0.68rem 0 0; color: rgba(240, 234, 214, 0.5); font-size: 0.76rem; line-height: 1.4; }
  .beta-download-page__loading { display: flex; width: fit-content; align-items: center; gap: 0.52rem; }
  .beta-download-page__loading svg { animation: beta-download-spin 950ms linear infinite; }
  .beta-download-page__error { color: #e5b1a7; }
  .beta-download-page__actions { display: flex; flex-wrap: wrap; gap: 1.1rem; margin-top: 1.25rem; }
  .beta-download-page__actions a, .beta-download-page__actions button { display: inline-flex; align-items: center; gap: 0.35rem; border: 0; background: transparent; padding: 0; color: rgba(240, 234, 214, 0.7); font-size: 0.8rem; text-decoration: none; cursor: pointer; }
  .beta-download-page__actions a:hover, .beta-download-page__actions button:hover { color: #c79f4a; }
  @keyframes beta-download-spin { to { transform: rotate(360deg); } }
  @media (max-width: 520px) { .beta-download-page { padding: 1.3rem; } .beta-download-page__header { align-items: flex-start; } .beta-download-page__eyebrow { padding-top: 0.15rem; } .beta-download-page__body { margin: 0; } .beta-download-page__primary { width: 100%; } }
`;
